// Rahmen aller Portale: dunkle Fläche, feste Kopfzeile (Safe-Area oben) und Inhalt, der
// um die GEMESSENE Kopfhöhe nach unten rückt – die Kopfzeile kann umbrechen oder wachsen,
// ohne Inhalt zu verdecken. Tooltips über data-tip (GlasTooltipSchicht, nur mit Maus).
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SignOut, type Icon } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import { GlasTooltipSchicht } from '../ui/GlasTooltip';

export interface PortalTab<K extends string> {
  key: K;
  label: string;
  icon: Icon;
  tip: string;
  /** Zähler-Badge; 0 oder leer = kein Badge */
  anzahl?: number;
}

export function PortalTabs<K extends string>({ tabs, aktiv, onWahl, label }: {
  tabs: PortalTab<K>[]; aktiv: K; onWahl: (k: K) => void; label: string;
}) {
  return (
    <nav className="p-tabs" aria-label={label}>
      {tabs.map((t) => {
        const an = t.key === aktiv;
        const Symbol = t.icon;
        return (
          <button
            key={t.key}
            type="button"
            className={`p-tab ${an ? 'p-tab-an' : ''}`}
            aria-current={an ? 'page' : undefined}
            aria-label={t.anzahl ? `${t.label} (${t.anzahl})` : t.label}
            data-tip={t.tip}
            onClick={() => onWahl(t.key)}
          >
            <Symbol size={17} weight={an ? 'fill' : 'regular'} />
            <span className="p-tab-label">{t.label}</span>
            {!!t.anzahl && <span className="p-tab-zahl" aria-hidden>{t.anzahl}</span>}
          </button>
        );
      })}
    </nav>
  );
}

/** Initialen-Avatar mit Status-Punkt (z. B. eingestempelt). */
export function PortalAvatar({ name, tip, punkt }: { name: string; tip: string; punkt?: 'an' | 'aus' }) {
  const initialen = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
  return (
    <div className="p-avatar" data-tip={tip} aria-label={tip} role="img">
      {initialen}
      {punkt && (
        <span
          className={`p-avatar-punkt ${punkt === 'an' ? 'p-puls' : ''}`}
          style={{ background: punkt === 'an' ? 'var(--p-ok)' : 'var(--p-w30)' }}
        />
      )}
    </div>
  );
}

export function PortalShell({ links, mitte, rechts, breite = 'weit', tabsZeile = false, children }: {
  links?: React.ReactNode;
  /** Segment-Tabs; ohne Tabs wird die Kopfzeile zweispaltig */
  mitte?: React.ReactNode;
  rechts?: React.ReactNode;
  /** weit 1560 (Hausmeister-Listen), portal 1400 (Mieter/Eigentümer), schmal 920 (Detail) */
  breite?: 'weit' | 'portal' | 'schmal';
  /** Viele Tabs: unter 760 px in eigener Zeile unter Logo und Aktionen */
  tabsZeile?: boolean;
  children: React.ReactNode;
}) {
  const kopfRef = useRef<HTMLElement>(null);
  const [kopfHoehe, setKopfHoehe] = useState(72);

  useLayoutEffect(() => {
    const el = kopfRef.current;
    if (!el) return;
    const messen = () => setKopfHoehe(Math.ceil(el.getBoundingClientRect().height));
    messen();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', messen);
      return () => window.removeEventListener('resize', messen);
    }
    const ro = new ResizeObserver(messen);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const breiteKlasse = { weit: 'p-breite-weit', portal: 'p-breite-portal', schmal: 'p-breite-schmal' }[breite];

  return (
    <div data-theme="dark" className="portal-flaeche">
      <GlasTooltipSchicht />
      <header ref={kopfRef} className="p-kopf">
        <div className={`p-kopf-leiste ${breiteKlasse} ${mitte ? '' : 'p-ohne-tabs'} ${tabsZeile ? 'p-tabs-zeile' : ''}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>{links}</div>
          {mitte}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>{rechts}</div>
        </div>
      </header>
      <main className={`p-inhalt ${breiteKlasse}`} style={{ paddingTop: kopfHoehe + 16 }}>
        {children}
      </main>
    </div>
  );
}

/** Logo mit Schriftzug „<Portal> · Immo Schwede“ (Schriftzug ab 1100 px). */
export function PortalMarke({ portal }: { portal: string }) {
  return (
    <div className="p-marke">
      <img src="/app-icon-192.png" alt="Immo Schwede" data-tip={`Immo Schwede · ${portal}`} />
      <span className="p-marke-text">
        <span style={{ fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap' }}>{portal}</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--p-w45)' }}>Immo Schwede</span>
      </span>
    </div>
  );
}

/** Avatar mit Menü: Name, E-Mail, Abmelden. Schließt bei Esc und Klick daneben. */
export function PortalProfilMenue({ name, email, tip, abmeldenTip }: { name: string; email: string; tip: string; abmeldenTip: string }) {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!offen) return;
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    const klick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOffen(false); };
    document.addEventListener('keydown', taste);
    document.addEventListener('mousedown', klick);
    return () => { document.removeEventListener('keydown', taste); document.removeEventListener('mousedown', klick); };
  }, [offen]);
  const initialen = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="p-avatar-knopf" data-tip={tip} aria-label={tip} aria-haspopup="menu" aria-expanded={offen} onClick={() => setOffen((o) => !o)}>
        {initialen}
      </button>
      {offen && (
        <div className="p-menue p-rein" role="menu">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '8px 10px 10px', minWidth: 0 }}>
            <span className="p-kuerzen" style={{ fontSize: 14, fontWeight: 800 }}>{name}</span>
            {email && <span className="p-kuerzen" style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>{email}</span>}
          </div>
          <button type="button" role="menuitem" className="p-menue-abmelden" data-tip={abmeldenTip} onClick={() => void supabase.auth.signOut()}>
            <SignOut size={18} /> Abmelden
          </button>
        </div>
      )}
    </div>
  );
}
